import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    updateCheckInStaffAccessStatusById,
    regenerateInviteCode,
    attachTicketTypeIds,
    removeTicketTypeIds
} from '../../../../../../app/store/slices/event/team/team';
import { successToastTopRight, errorToast } from '../../../../../../utils/toasts/toasts';

export default function useCheckInStaffList(data) {
    const dispatch = useDispatch();
    const { tickets } = useSelector((state) => state.ticket);
    const { checkInStaffList } = useSelector(state => state.team);

    const [openDialog, setOpenDialog] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);

    const [selectedInvitationId, setSelectedInvitationId] = useState(null);
    const [selectedTicketIds, setSelectedTicketIds] = useState([]);

    const [loading, setLoading] = useState(false);
    const [regeneratedStates, setRegeneratedStates] = useState({});

    // console.log("CheckInStaffList -> ", JSON.stringify(checkInStaffList?.data, null, 2))

    /**
     * @function to handle menu item clicks for each staff member
     */
    function handleMenuItemClick(_, optionIndex, rowIndex) {
        if (optionIndex === 1 && data[rowIndex].access) {
            setSelectedInvitationId(data[rowIndex].id);
            setOpenDialog(true);
            return;
        }
    }


    /**
     * @async function: to confirm and revoke access for a staff member
     * Calls the API and updates local state/UI accordingly
     * Handles loading state and errors
     * 
     * @returns {void}
     */
    async function handleConfirmRevoke() {

        if (!selectedInvitationId) return;

        try {
            setLoading(true);

            await dispatch(
                updateCheckInStaffAccessStatusById({
                    invitationId: selectedInvitationId,
                    payload: { access: false }
                })
            ).unwrap();

            setOpenDialog(false);
            setSelectedInvitationId(null);

            successToastTopRight("Access revoked successfully");
        } catch (error) {
            errorToast(error?.data?.message || "Failed to revoke access");
        } finally {
            setLoading(false);
        }
    }


    /**
     * @async function: to regenerate the invite code for a staff member
     * Calls the API and updates local state/UI accordingly
     * Handles loading state and errors
     * Marks the staff member as "Regenerated" for 1 minute after successful regeneration
     * 
     * @param {number} recordId - The ID of the staff member whose invite code is to be regenerated
     * 
     * @returns {void}
     */
    async function reGenerateInviteCode(recordId) {
        setLoading(true);
        try {
            setSelectedInvitationId(recordId);
            await dispatch(regenerateInviteCode({ recordId })).unwrap();

            setRegeneratedStates((prev) => ({ ...prev, [recordId]: true }));

            setTimeout(() => {
                setRegeneratedStates((prev) => ({ ...prev, [recordId]: false }));
            }, 1 * 60 * 1000);

            successToastTopRight("Invitation code regenerated successfully");
        } catch (error) {
            errorToast("Failed to regenerate invitation code");
        } finally {
            setLoading(false);
        }
    }


    /**
     * @function to handle cancel action in the alert dialog
     * Closes the dialog and resets the selected invitation ID
     *
     * @returns {void}
     */
    function handleAlertDialogCancel() {
        setOpenDialog(false);
        setSelectedInvitationId(null);
    }


    /**
     * @function to close the drawer and reset relevant states
     * 
     * @returns {void}
     */
    function closeDrawer() {
        setOpenDrawer(false);
        setSelectedInvitationId(null);
        setSelectedTicketIds([]);
    }


    /**
     * @function to handle selection/deselection of individual tickets
     * Updates the selectedTicketIds state based on checkbox changes
     * 
     * @param {object} event - The change event from the checkbox
     * @param {number} ticketId - The ID of the ticket being selected/deselected
     * 
     * @returns {void}
     */
    function handleTicketsSelection(event, ticketId) {
        const isChecked = event.target.checked;

        setSelectedTicketIds((prev) => {
            if (isChecked) {
                return [...prev, ticketId];
            } else {
                return prev.filter((id) => id !== ticketId);
            }
        });
    }


    /**
     * @async function: to submit the selected ticket IDs for the staff member
     * 1. Attach all currently selected IDs
     * 2. Compare with already assigned ticket IDs
     * 3. If some IDs were deselected, call removeTicketTypeIds
     * 
     * @example
     * Suppose admin is setting permissions for staffId = 10
     * Already assigned tickets = [1, 2, 3]
     * Admin selects tickets = [2, 3, 4]
     * 
     * Step 1: Attach [2, 3, 4]
     *   -> API POST /invitations/10/tickets { ticket_type_ids: [2, 3, 4] }
     * 
     * Step 2: Compare with already assigned [1, 2, 3]
     *   -> Removed = [1] (because 1 is not in new selection)
     * 
     * Step 3: Call removeTicketTypeIds for [1]
     *   -> API DELETE /invitations/10/tickets { ticket_type_ids: [1] }
     * 
     * Final result:
     *   StaffId 10 has ticket permissions [2, 3, 4]
     *   Ticket 1 access revoked
     * 
     * @returns {void}
     */
    async function handleSubmitDetails() {
        if (!selectedInvitationId) return;

        try {
            setLoading(true);

            // Step 1: Attach currently selected ticket IDs
            await dispatch(attachTicketTypeIds({
                invitationId: selectedInvitationId,
                payload: { ticket_type_ids: selectedTicketIds }
            })).unwrap();

            // Step 2: Get staff's currently assigned tickets from redux
            const staff = checkInStaffList?.data?.find(staff => staff.id === selectedInvitationId);
            const alreadyAssignedIds = staff?.ticket_types?.map(t => t.ticket_type_id) || [];

            // Step 3: Find tickets that were removed
            const removedIds = alreadyAssignedIds.filter(id => !selectedTicketIds.includes(id));

            // Step 4: If there are removed IDs, call remove API
            if (removedIds.length > 0) {
                await dispatch(removeTicketTypeIds({
                    invitationId: selectedInvitationId,
                    payload: { ticket_type_ids: removedIds }
                })).unwrap();
            }

            successToastTopRight("Permissions updated successfully");
            closeDrawer();
        } catch (error) {
            errorToast("Failed to update permissions");
        } finally {
            setLoading(false);
        }
    }



    /**
     * @function to handle the click of "Set Permissions" action for a staff member
     * 1. Opens the drawer
     * 2. Sets the selected invitation ID
     * 3. Find the staff member from the list and pre-select their ticket IDs if any
     * 4. If no ticket types are assigned, selectedTicketIds will be an empty array
     * 
     * @param {number} recordId - The ID of the staff member whose permissions are to be set
     * @returns {void}
     */
    function handleStaffPermission(recordId) {
        setSelectedInvitationId(recordId);

        const staff = checkInStaffList?.data?.find(staff => staff.id === recordId);

        if (staff && staff.ticket_types) {
            const preSelectedIds = staff.ticket_types.map(t => t.ticket_type_id);
            setSelectedTicketIds(preSelectedIds);
        } else {
            setSelectedTicketIds([]);
        }

        setOpenDrawer(true);
    }


    /**
     * @function to handle select/deselect all tickets
     * 1. If all tickets are already selected, deselect all
     * 2. If not all tickets are selected, select all ticket IDs
     * 
     * @returns {void}
     */
    function handleSelectAllTickets() {
        if (selectedTicketIds.length === tickets.length) {
            setSelectedTicketIds([]);
        } else {
            const allTicketIds = tickets.map(t => t.ticket_id);
            setSelectedTicketIds(allTicketIds);
        }
    }


    /**
     * Return all necessary states and handlers
     */
    return {
        handleMenuItemClick,
        handleConfirmRevoke,
        reGenerateInviteCode,
        loading,
        regeneratedStates,
        selectedInvitationId,
        setSelectedInvitationId,
        handleAlertDialogCancel,
        openDialog,
        openDrawer,
        closeDrawer,
        handleSubmitDetails,
        selectedTicketIds,
        tickets,
        handleTicketsSelection,
        handleStaffPermission,
        handleSelectAllTickets
    }
}
